import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { DocumentUploadPanel } from '../components/DocumentUploadPanel';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    Users, 
    Plus, 
    Eye, 
    Edit,
    Trash2,
    Search,
    FileText,
    AlertTriangle,
    CheckCircle,
    XCircle,
    Ship,
    Calendar,
    Phone,
    Mail,
    MapPin,
    Anchor,
    Upload,
    Download,
    UserCheck,
    UserX,
    Clock,
    BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const EMPRESAS_VINCULADAS = ['Delima Rio de Janeiro', 'Delima Pernambuco', 'Delima Belém', 'Reicon'];

const funcaoLabels = {
    comandante: 'Comandante',
    imediato: 'Imediato',
    marinheiro: 'Marinheiro',
    moco_conves: 'Moço de Convés',
    chefe_maquinas: 'Chefe de Máquinas',
    motorista: 'Motorista',
    cozinheiro: 'Cozinheiro',
    taifeiro: 'Taifeiro',
    auxiliar_maquinas: 'Auxiliar de Máquinas',
    condutor: 'Condutor',
    outros: 'Outros'
};

const categoriaLabels = {
    oficial_nautica: 'Oficial de Náutica',
    oficial_maquinas: 'Oficial de Máquinas',
    mestranca: 'Mestrança',
    subalternos_conves: 'Subalternos de Convés',
    subalternos_maquinas: 'Subalternos de Máquinas',
    outros: 'Outros'
};

const statusLabels = {
    ativo: { label: 'Ativo', class: 'bg-green-600' },
    inativo: { label: 'Inativo', class: 'bg-slate-600' },
    afastado: { label: 'Afastado', class: 'bg-yellow-600' },
    ferias: { label: 'Férias', class: 'bg-blue-600' },
    licenca: { label: 'Licença', class: 'bg-purple-600' }
};

const tipoAsoLabels = {
    admissional: 'Admissional',
    periodico: 'Periódico',
    retorno_trabalho: 'Retorno ao Trabalho',
    mudanca_funcao: 'Mudança de Função',
    demissional: 'Demissional'
};

const escalaLabels = {
    '60x12': '60x12',
    '28x28': '28x28',
    '30x15': '30x15',
    '21x21': '21x21',
    '14x14': '14x14',
    'outros': 'Outros'
};

const TripulantesPage = () => {
    const [tripulantes, setTripulantes] = useState([]);
    const [dashboard, setDashboard] = useState(null);
    const [empurradores, setEmpurradores] = useState([]);
    const [balsas, setBalsas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('lista');
    
    // Dialogs
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [cirDialogOpen, setCirDialogOpen] = useState(false);
    const [asoDialogOpen, setAsoDialogOpen] = useState(false);
    const [embarqueDialogOpen, setEmbarqueDialogOpen] = useState(false);
    
    const [selectedTripulante, setSelectedTripulante] = useState(null);
    const [editingId, setEditingId] = useState(null);
    
    const { hasRole } = useAuth();

    // Filtros
    const [filters, setFilters] = useState({
        busca: '',
        status: '',
        funcao: '',
        empresa: '',
        documentacao_irregular: false
    });

    // Form data
    const [formData, setFormData] = useState({
        nome: '',
        cpf: '',
        data_nascimento: '',
        nacionalidade: 'Brasileira',
        telefone: '',
        email: '',
        endereco: '',
        funcao: 'marinheiro',
        categoria_aquaviario: 'subalternos_conves',
        grupo_maritimo: 'fluvial',
        empresa_vinculada: '',
        status: 'ativo'
    });

    const [cirData, setCirData] = useState({
        numero_cir: '',
        categoria_cir: '',
        orgao_emissor: 'capitania_portos',
        data_emissao: '',
        data_validade: ''
    });

    const [asoData, setAsoData] = useState({
        tipo_aso: 'periodico',
        data_exame: '',
        data_validade: '',
        medico_responsavel: '',
        crm_medico: '',
        clinica_responsavel: '',
        observacoes: ''
    });

    const [embarqueData, setEmbarqueData] = useState({
        embarcacao_tipo: 'empurrador',
        embarcacao_id: '',
        funcao_bordo: 'marinheiro',
        data_embarque: '',
        data_prevista_desembarque: '',
        tipo_escala: '28x28',
        status_lotacao: 'embarcado'
    });

    const fetchTripulantes = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.busca) params.append('busca', filters.busca);
            if (filters.status) params.append('status', filters.status);
            if (filters.funcao) params.append('funcao', filters.funcao);
            if (filters.empresa) params.append('empresa', filters.empresa);
            if (filters.documentacao_irregular) params.append('documentacao_irregular', 'true');
            
            const res = await axios.get(`${API}/tripulantes?${params.toString()}`);
            setTripulantes(res.data);
        } catch (error) {
            toast.error('Erro ao carregar tripulantes');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const fetchDashboard = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/tripulantes/dashboard`);
            setDashboard(res.data);
        } catch (error) {
            console.error('Erro ao carregar dashboard:', error);
        }
    }, []);

    const fetchBaseData = async () => {
        try {
            const [empRes, balsasRes] = await Promise.all([
                axios.get(`${API}/empurradores`),
                axios.get(`${API}/balsas`)
            ]);
            setEmpurradores(empRes.data);
            setBalsas(balsasRes.data);
        } catch (error) {
            console.error('Erro ao carregar dados base:', error);
        }
    };

    useEffect(() => {
        fetchBaseData();
    }, []);

    useEffect(() => {
        fetchTripulantes();
        fetchDashboard();
    }, [fetchTripulantes, fetchDashboard]);

    const handleCreateTripulante = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await axios.put(`${API}/tripulantes/${editingId}`, formData);
                toast.success('Tripulante atualizado');
            } else {
                await axios.post(`${API}/tripulantes`, formData);
                toast.success('Tripulante cadastrado');
            }
            setDialogOpen(false);
            resetForm();
            fetchTripulantes();
            fetchDashboard();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar tripulante');
        }
    };

    const handleSaveCir = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/tripulantes/${selectedTripulante.id}/cir`, cirData);
            toast.success('CIR atualizada');
            setCirDialogOpen(false);
            fetchTripulanteDetails(selectedTripulante.id);
            fetchTripulantes();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar CIR');
        }
    };

    const handleSaveAso = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/tripulantes/${selectedTripulante.id}/aso`, asoData);
            toast.success('ASO adicionado');
            setAsoDialogOpen(false);
            resetAsoForm();
            fetchTripulanteDetails(selectedTripulante.id);
            fetchTripulantes();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar ASO');
        }
    };

    const handleCreateEmbarque = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/embarques`, {
                ...embarqueData,
                tripulante_id: selectedTripulante.id
            });
            toast.success('Embarque registrado');
            setEmbarqueDialogOpen(false);
            resetEmbarqueForm();
            fetchTripulanteDetails(selectedTripulante.id);
            fetchDashboard();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao registrar embarque');
        }
    };

    const handleDesembarcar = async (embarqueId) => {
        if (!window.confirm('Confirma o desembarque deste tripulante?')) return;
        
        try {
            await axios.post(`${API}/embarques/${embarqueId}/desembarcar`, {
                data_desembarque: new Date().toISOString().split('T')[0],
                motivo_desembarque: 'Fim de escala'
            });
            toast.success('Desembarque registrado');
            fetchTripulanteDetails(selectedTripulante.id);
            fetchDashboard();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao registrar desembarque');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Tem certeza que deseja excluir este tripulante?')) return;
        
        try {
            await axios.delete(`${API}/tripulantes/${id}`);
            toast.success('Tripulante excluído');
            fetchTripulantes();
            fetchDashboard();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao excluir tripulante');
        }
    };

    const fetchTripulanteDetails = async (id) => {
        try {
            const res = await axios.get(`${API}/tripulantes/${id}`);
            setSelectedTripulante(res.data);
        } catch (error) {
            toast.error('Erro ao carregar detalhes');
        }
    };

    const openDetails = async (trip) => {
        await fetchTripulanteDetails(trip.id);
        setDetailDialogOpen(true);
    };

    const openEdit = (trip) => {
        setEditingId(trip.id);
        setFormData({
            nome: trip.nome || '',
            cpf: trip.cpf || '',
            data_nascimento: trip.data_nascimento || '',
            nacionalidade: trip.nacionalidade || 'Brasileira',
            telefone: trip.telefone || '',
            email: trip.email || '',
            endereco: trip.endereco || '',
            funcao: trip.funcao || 'marinheiro',
            categoria_aquaviario: trip.categoria_aquaviario || 'subalternos_conves',
            grupo_maritimo: trip.grupo_maritimo || 'fluvial',
            empresa_vinculada: trip.empresa_vinculada || '',
            status: trip.status || 'ativo'
        });
        setDialogOpen(true);
    };

    const openCirDialog = () => {
        if (selectedTripulante?.cir) {
            setCirData({
                numero_cir: selectedTripulante.cir.numero_cir || '',
                categoria_cir: selectedTripulante.cir.categoria_cir || '',
                orgao_emissor: selectedTripulante.cir.orgao_emissor || 'capitania_portos',
                data_emissao: selectedTripulante.cir.data_emissao || '',
                data_validade: selectedTripulante.cir.data_validade || ''
            });
        }
        setCirDialogOpen(true);
    };

    const resetForm = () => {
        setEditingId(null);
        setFormData({
            nome: '',
            cpf: '',
            data_nascimento: '',
            nacionalidade: 'Brasileira',
            telefone: '',
            email: '',
            endereco: '',
            funcao: 'marinheiro',
            categoria_aquaviario: 'subalternos_conves',
            grupo_maritimo: 'fluvial',
            empresa_vinculada: '',
            status: 'ativo'
        });
    };

    const resetAsoForm = () => {
        setAsoData({
            tipo_aso: 'periodico',
            data_exame: '',
            data_validade: '',
            medico_responsavel: '',
            crm_medico: '',
            clinica_responsavel: '',
            observacoes: ''
        });
    };

    const resetEmbarqueForm = () => {
        setEmbarqueData({
            embarcacao_tipo: 'empurrador',
            embarcacao_id: '',
            funcao_bordo: 'marinheiro',
            data_embarque: '',
            data_prevista_desembarque: '',
            tipo_escala: '28x28',
            status_lotacao: 'embarcado'
        });
    };

    const getEmbarcacoes = (tipo) => {
        if (tipo === 'empurrador') return empurradores;
        if (tipo === 'balsa') return balsas;
        return [];
    };

    const getDocStatusIcon = (docStatus) => {
        if (!docStatus) return <XCircle className="w-4 h-4 text-slate-400" />;
        if (docStatus.documentacao_valida) return <CheckCircle className="w-4 h-4 text-green-500" />;
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2">
                        <Users className="w-6 h-6 text-cyan-400" />
                        Controle de Tripulantes
                    </h1>
                    <p className="text-slate-400 text-sm mt-1">
                        Gestão completa de tripulações e documentos
                    </p>
                </div>
                {hasRole(['admin', 'gestor', 'operador']) && (
                    <Button 
                        onClick={() => { resetForm(); setDialogOpen(true); }}
                        className="bg-cyan-600 hover:bg-cyan-700"
                        data-testid="btn-novo-tripulante"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Novo Tripulante
                    </Button>
                )}
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-slate-800 border-slate-700">
                    <TabsTrigger value="lista" className="data-[state=active]:bg-cyan-600">
                        <Users className="w-4 h-4 mr-2" />
                        Tripulantes
                    </TabsTrigger>
                    <TabsTrigger value="dashboard" className="data-[state=active]:bg-cyan-600">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Dashboard
                    </TabsTrigger>
                </TabsList>

                {/* Dashboard Tab */}
                <TabsContent value="dashboard" className="space-y-6">
                    {dashboard && (
                        <>
                            {/* KPIs */}
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                <Card className="bg-slate-900 border-slate-800">
                                    <CardContent className="p-4 text-center">
                                        <p className="text-3xl font-bold text-white">{dashboard.total_tripulantes}</p>
                                        <p className="text-slate-400 text-sm">Total</p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-slate-900 border-slate-800">
                                    <CardContent className="p-4 text-center">
                                        <p className="text-3xl font-bold text-green-400">{dashboard.ativos}</p>
                                        <p className="text-slate-400 text-sm">Ativos</p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-slate-900 border-slate-800">
                                    <CardContent className="p-4 text-center">
                                        <p className="text-3xl font-bold text-blue-400">{dashboard.embarcados}</p>
                                        <p className="text-slate-400 text-sm">Embarcados</p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-slate-900 border-slate-800">
                                    <CardContent className="p-4 text-center">
                                        <p className="text-3xl font-bold text-yellow-400">{dashboard.afastados}</p>
                                        <p className="text-slate-400 text-sm">Afastados</p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-slate-900 border-red-800 border-2">
                                    <CardContent className="p-4 text-center">
                                        <p className="text-3xl font-bold text-red-400">{dashboard.documentos?.documentacao_irregular || 0}</p>
                                        <p className="text-slate-400 text-sm">Doc. Irregular</p>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Efetivo por Empresa */}
                            {dashboard.por_empresa?.length > 0 && (
                                <Card className="bg-slate-900 border-slate-800" data-testid="card-efetivo-empresa">
                                    <CardHeader>
                                        <CardTitle className="text-white text-lg flex items-center gap-2">
                                            <Users className="w-5 h-5 text-cyan-400" />
                                            Efetivo por Empresa
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                            {dashboard.por_empresa.map((emp) => (
                                                <div key={emp.empresa} className="p-4 bg-slate-800/50 rounded border border-slate-700">
                                                    <p className="text-slate-300 text-sm font-medium truncate" title={emp.empresa}>{emp.empresa}</p>
                                                    <p className="text-3xl font-bold text-cyan-400 mt-1">{emp.total}</p>
                                                    <div className="flex justify-between text-xs mt-2">
                                                        <span className="text-green-400">{emp.ativos} ativos</span>
                                                        <span className="text-blue-400">{emp.embarcados} embarcados</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Documentos */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Card className="bg-slate-900 border-slate-800">
                                    <CardHeader>
                                        <CardTitle className="text-white text-lg flex items-center gap-2">
                                            <FileText className="w-5 h-5 text-cyan-400" />
                                            Status CIR
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        <div className="flex justify-between items-center p-3 bg-red-900/20 rounded border border-red-800">
                                            <span className="text-slate-300">CIRs Vencidas</span>
                                            <span className="text-red-400 font-bold">{dashboard.documentos?.cir_vencidas || 0}</span>
                                        </div>
                                        <div className="flex justify-between items-center p-3 bg-yellow-900/20 rounded border border-yellow-800">
                                            <span className="text-slate-300">CIRs Vencendo (90 dias)</span>
                                            <span className="text-yellow-400 font-bold">{dashboard.documentos?.cir_vencendo || 0}</span>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card className="bg-slate-900 border-slate-800">
                                    <CardHeader>
                                        <CardTitle className="text-white text-lg flex items-center gap-2">
                                            <FileText className="w-5 h-5 text-green-400" />
                                            Status ASO
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        <div className="flex justify-between items-center p-3 bg-red-900/20 rounded border border-red-800">
                                            <span className="text-slate-300">ASOs Vencidos</span>
                                            <span className="text-red-400 font-bold">{dashboard.documentos?.aso_vencidos || 0}</span>
                                        </div>
                                        <div className="flex justify-between items-center p-3 bg-yellow-900/20 rounded border border-yellow-800">
                                            <span className="text-slate-300">ASOs Vencendo (90 dias)</span>
                                            <span className="text-yellow-400 font-bold">{dashboard.documentos?.aso_vencendo || 0}</span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Alertas */}
                            {dashboard.alertas?.length > 0 && (
                                <Card className="bg-slate-900 border-red-800">
                                    <CardHeader>
                                        <CardTitle className="text-red-400 flex items-center gap-2">
                                            <AlertTriangle className="w-5 h-5" />
                                            Alertas ({dashboard.alertas.length})
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-2 max-h-60 overflow-y-auto">
                                            {dashboard.alertas.map((alerta, idx) => (
                                                <div 
                                                    key={idx} 
                                                    className={`flex justify-between items-center p-3 rounded border ${
                                                        alerta.nivel === 'critical' 
                                                            ? 'bg-red-900/20 border-red-800' 
                                                            : 'bg-yellow-900/20 border-yellow-800'
                                                    }`}
                                                >
                                                    <span className="text-slate-300">{alerta.tripulante_nome}</span>
                                                    <span className={alerta.nivel === 'critical' ? 'text-red-400' : 'text-yellow-400'}>
                                                        {alerta.mensagem}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Tripulantes por Embarcação */}
                            {dashboard.tripulantes_por_embarcacao?.length > 0 && (
                                <Card className="bg-slate-900 border-slate-800">
                                    <CardHeader>
                                        <CardTitle className="text-white flex items-center gap-2">
                                            <Ship className="w-5 h-5 text-cyan-400" />
                                            Tripulantes por Embarcação
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {dashboard.tripulantes_por_embarcacao.map((emb) => (
                                                <div key={emb.embarcacao_id} className="p-4 bg-slate-800/50 rounded border border-slate-700">
                                                    <div className="flex justify-between items-center mb-3">
                                                        <h4 className="text-white font-medium">{emb.embarcacao_nome}</h4>
                                                        <span className="text-cyan-400 font-bold">{emb.total}</span>
                                                    </div>
                                                    <div className="space-y-1">
                                                        {emb.tripulantes.slice(0, 5).map((trip, idx) => (
                                                            <div key={idx} className="flex justify-between text-sm">
                                                                <span className="text-slate-400">{trip.tripulante_nome}</span>
                                                                <span className="text-slate-500">{funcaoLabels[trip.funcao] || trip.funcao}</span>
                                                            </div>
                                                        ))}
                                                        {emb.tripulantes.length > 5 && (
                                                            <p className="text-slate-500 text-xs">+{emb.tripulantes.length - 5} mais...</p>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </>
                    )}
                </TabsContent>

                {/* Lista Tab */}
                <TabsContent value="lista" className="space-y-4">
                    {/* Filters */}
                    <Card className="bg-slate-900 border-slate-800">
                        <CardContent className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                                <div className="md:col-span-2">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <Input 
                                            placeholder="Buscar por nome, CPF ou CIR..."
                                            value={filters.busca}
                                            onChange={(e) => setFilters(f => ({ ...f, busca: e.target.value }))}
                                            className="bg-slate-800 border-slate-700 text-white pl-10"
                                        />
                                    </div>
                                </div>
                                <Select value={filters.status || "all"} onValueChange={(v) => setFilters(f => ({ ...f, status: v === "all" ? "" : v }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos Status</SelectItem>
                                        {Object.entries(statusLabels).map(([key, val]) => (
                                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select value={filters.funcao || "all"} onValueChange={(v) => setFilters(f => ({ ...f, funcao: v === "all" ? "" : v }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Função" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas Funções</SelectItem>
                                        {Object.entries(funcaoLabels).map(([key, label]) => (
                                            <SelectItem key={key} value={key}>{label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select value={filters.empresa || "all"} onValueChange={(v) => setFilters(f => ({ ...f, empresa: v === "all" ? "" : v }))}>
                                    <SelectTrigger data-testid="filter-empresa-tripulante" className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Empresa" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas Empresas</SelectItem>
                                        {EMPRESAS_VINCULADAS.map((emp) => (
                                            <SelectItem key={emp} value={emp}>{emp}</SelectItem>
                                        ))}
                                        <SelectItem value="Delima Navegação">Delima Navegação (legado)</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Button 
                                    variant={filters.documentacao_irregular ? "destructive" : "outline"}
                                    onClick={() => setFilters(f => ({ ...f, documentacao_irregular: !f.documentacao_irregular }))}
                                    className="border-slate-700"
                                >
                                    <AlertTriangle className="w-4 h-4 mr-2" />
                                    Doc. Irregular
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Table */}
                    <Card className="bg-slate-900 border-slate-800">
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-slate-800 hover:bg-slate-800/50">
                                            <TableHead className="text-slate-400">Nome</TableHead>
                                            <TableHead className="text-slate-400">CPF</TableHead>
                                            <TableHead className="text-slate-400">Função</TableHead>
                                            <TableHead className="text-slate-400">Empresa</TableHead>
                                            <TableHead className="text-slate-400">Status</TableHead>
                                            <TableHead className="text-slate-400">CIR</TableHead>
                                            <TableHead className="text-slate-400">Documentação</TableHead>
                                            <TableHead className="text-slate-400 text-right">Ações</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {loading ? (
                                            <TableRow>
                                                <TableCell colSpan={8} className="text-center text-slate-400 py-8">
                                                    Carregando...
                                                </TableCell>
                                            </TableRow>
                                        ) : tripulantes.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={8} className="text-center text-slate-400 py-8">
                                                    Nenhum tripulante encontrado
                                                </TableCell>
                                            </TableRow>
                                        ) : tripulantes.map((trip) => (
                                            <TableRow key={trip.id} className="border-slate-800 hover:bg-slate-800/50">
                                                <TableCell className="text-white font-medium">{trip.nome}</TableCell>
                                                <TableCell className="text-slate-300 font-mono">{trip.cpf}</TableCell>
                                                <TableCell className="text-slate-300">{funcaoLabels[trip.funcao] || trip.funcao}</TableCell>
                                                <TableCell className="text-slate-400 text-sm">{trip.empresa_vinculada || '-'}</TableCell>
                                                <TableCell>
                                                    <span className={`px-2 py-1 rounded text-xs text-white ${statusLabels[trip.status]?.class || 'bg-slate-600'}`}>
                                                        {statusLabels[trip.status]?.label || trip.status}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-slate-300 font-mono text-sm">
                                                    {trip.cir?.numero_cir || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        {getDocStatusIcon(trip.documentacao_status)}
                                                        <span className={`text-sm ${trip.documentacao_status?.documentacao_valida ? 'text-green-400' : 'text-red-400'}`}>
                                                            {trip.documentacao_status?.documentacao_valida ? 'Regular' : 'Irregular'}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex gap-1 justify-end">
                                                        <Button variant="ghost" size="sm" onClick={() => openDetails(trip)}>
                                                            <Eye className="w-4 h-4 text-slate-400" />
                                                        </Button>
                                                        {hasRole(['admin', 'gestor', 'operador']) && (
                                                            <Button variant="ghost" size="sm" onClick={() => openEdit(trip)}>
                                                                <Edit className="w-4 h-4 text-slate-400" />
                                                            </Button>
                                                        )}
                                                        {hasRole(['admin']) && (
                                                            <Button variant="ghost" size="sm" onClick={() => handleDelete(trip.id)}>
                                                                <Trash2 className="w-4 h-4 text-red-400" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Dialog: Novo/Editar Tripulante */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Users className="w-5 h-5 text-cyan-400" />
                            {editingId ? 'Editar Tripulante' : 'Novo Tripulante'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateTripulante} className="space-y-4">
                        <div className="border-b border-slate-700 pb-2 mb-4">
                            <h3 className="text-slate-300 font-medium">Informações Pessoais</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <Label className="text-slate-300">Nome Completo *</Label>
                                <Input 
                                    value={formData.nome}
                                    onChange={(e) => setFormData(f => ({ ...f, nome: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    required
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">CPF *</Label>
                                <Input 
                                    value={formData.cpf}
                                    onChange={(e) => setFormData(f => ({ ...f, cpf: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    placeholder="000.000.000-00"
                                    required
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">Data de Nascimento</Label>
                                <Input 
                                    type="date"
                                    value={formData.data_nascimento}
                                    onChange={(e) => setFormData(f => ({ ...f, data_nascimento: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">Nacionalidade</Label>
                                <Input 
                                    value={formData.nacionalidade}
                                    onChange={(e) => setFormData(f => ({ ...f, nacionalidade: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">Telefone</Label>
                                <Input 
                                    value={formData.telefone}
                                    onChange={(e) => setFormData(f => ({ ...f, telefone: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    placeholder="(00) 00000-0000"
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">E-mail</Label>
                                <Input 
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                />
                            </div>
                            <div className="col-span-2">
                                <Label className="text-slate-300">Endereço</Label>
                                <Input 
                                    value={formData.endereco}
                                    onChange={(e) => setFormData(f => ({ ...f, endereco: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                />
                            </div>
                        </div>

                        <div className="border-b border-slate-700 pb-2 mb-4 pt-4">
                            <h3 className="text-slate-300 font-medium">Informações Profissionais</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-slate-300">Função a Bordo *</Label>
                                <Select value={formData.funcao} onValueChange={(v) => setFormData(f => ({ ...f, funcao: v }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(funcaoLabels).map(([key, label]) => (
                                            <SelectItem key={key} value={key}>{label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-slate-300">Categoria do Aquaviário</Label>
                                <Select value={formData.categoria_aquaviario} onValueChange={(v) => setFormData(f => ({ ...f, categoria_aquaviario: v }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(categoriaLabels).map(([key, label]) => (
                                            <SelectItem key={key} value={key}>{label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-slate-300">Grupo Marítimo/Fluvial</Label>
                                <Select value={formData.grupo_maritimo} onValueChange={(v) => setFormData(f => ({ ...f, grupo_maritimo: v }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="maritimo">Marítimo</SelectItem>
                                        <SelectItem value="fluvial">Fluvial</SelectItem>
                                        <SelectItem value="lacustre">Lacustre</SelectItem>
                                        <SelectItem value="pescador">Pescador</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-slate-300">Status</Label>
                                <Select value={formData.status} onValueChange={(v) => setFormData(f => ({ ...f, status: v }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(statusLabels).map(([key, val]) => (
                                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="col-span-2">
                                <Label className="text-slate-300">Empresa Vinculada</Label>
                                <Select 
                                    value={formData.empresa_vinculada || ''} 
                                    onValueChange={(v) => setFormData(f => ({ ...f, empresa_vinculada: v }))}
                                >
                                    <SelectTrigger data-testid="select-empresa-vinculada" className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Selecione a empresa" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {EMPRESAS_VINCULADAS.map((emp) => (
                                            <SelectItem key={emp} value={emp}>{emp}</SelectItem>
                                        ))}
                                        {formData.empresa_vinculada && !EMPRESAS_VINCULADAS.includes(formData.empresa_vinculada) && (
                                            <SelectItem value={formData.empresa_vinculada}>{formData.empresa_vinculada} (legado)</SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-4">
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-700">
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700">
                                {editingId ? 'Salvar' : 'Cadastrar'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Dialog: Detalhes do Tripulante */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Users className="w-5 h-5 text-cyan-400" />
                            {selectedTripulante?.nome}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedTripulante && (
                        <div className="space-y-6">
                            {/* Info e Status */}
                            <div className="flex flex-wrap gap-2 items-center">
                                <span className={`px-3 py-1 rounded text-sm text-white ${statusLabels[selectedTripulante.status]?.class}`}>
                                    {statusLabels[selectedTripulante.status]?.label}
                                </span>
                                <span className={`px-3 py-1 rounded text-sm text-white ${selectedTripulante.documentacao_status?.documentacao_valida ? 'bg-green-600' : 'bg-red-600'}`}>
                                    {selectedTripulante.documentacao_status?.documentacao_valida ? 'Documentação Regular' : 'Documentação Irregular'}
                                </span>
                                {selectedTripulante.embarque_atual && (
                                    <span className="px-3 py-1 rounded text-sm text-white bg-blue-600 flex items-center gap-1">
                                        <Ship className="w-3 h-3" />
                                        Embarcado: {selectedTripulante.embarque_atual.embarcacao_nome}
                                    </span>
                                )}
                            </div>

                            {/* Documentos Upload Panel - destacado */}
                            {selectedTripulante && (
                                <DocumentUploadPanel modulo="tripulantes" entidadeId={selectedTripulante.id} entidadeNome={selectedTripulante.nome} />
                            )}

                            {/* Dados Pessoais */}
                            <Card className="bg-slate-800/50 border-slate-700">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-white text-sm">Dados Pessoais</CardTitle>
                                </CardHeader>
                                <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div>
                                        <p className="text-slate-400">CPF</p>
                                        <p className="text-white font-mono">{selectedTripulante.cpf}</p>
                                    </div>
                                    <div>
                                        <p className="text-slate-400">Nascimento</p>
                                        <p className="text-white">{selectedTripulante.data_nascimento || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-slate-400">Telefone</p>
                                        <p className="text-white">{selectedTripulante.telefone || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-slate-400">E-mail</p>
                                        <p className="text-white">{selectedTripulante.email || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-slate-400">Função</p>
                                        <p className="text-white">{funcaoLabels[selectedTripulante.funcao] || selectedTripulante.funcao}</p>
                                    </div>
                                    <div>
                                        <p className="text-slate-400">Categoria</p>
                                        <p className="text-white">{categoriaLabels[selectedTripulante.categoria_aquaviario] || selectedTripulante.categoria_aquaviario}</p>
                                    </div>
                                    <div>
                                        <p className="text-slate-400">Grupo</p>
                                        <p className="text-white capitalize">{selectedTripulante.grupo_maritimo}</p>
                                    </div>
                                    <div>
                                        <p className="text-slate-400">Empresa</p>
                                        <p className="text-white">{selectedTripulante.empresa_vinculada}</p>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* CIR */}
                            <Card className={`border ${selectedTripulante.cir?.status === 'vencido' ? 'border-red-600 bg-red-900/10' : selectedTripulante.cir?.status === 'a_vencer' ? 'border-yellow-600 bg-yellow-900/10' : 'border-slate-700 bg-slate-800/50'}`}>
                                <CardHeader className="pb-2 flex flex-row justify-between items-center">
                                    <CardTitle className="text-white text-sm flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-cyan-400" />
                                        CIR - Caderneta de Inscrição e Registro
                                    </CardTitle>
                                    {hasRole(['admin', 'gestor', 'operador']) && (
                                        <Button size="sm" variant="outline" onClick={openCirDialog} className="border-slate-600">
                                            <Edit className="w-3 h-3 mr-1" />
                                            {selectedTripulante.cir ? 'Editar' : 'Cadastrar'}
                                        </Button>
                                    )}
                                </CardHeader>
                                <CardContent>
                                    {selectedTripulante.cir ? (
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                            <div>
                                                <p className="text-slate-400">Número</p>
                                                <p className="text-white font-mono">{selectedTripulante.cir.numero_cir}</p>
                                            </div>
                                            <div>
                                                <p className="text-slate-400">Categoria</p>
                                                <p className="text-white">{selectedTripulante.cir.categoria_cir}</p>
                                            </div>
                                            <div>
                                                <p className="text-slate-400">Emissão</p>
                                                <p className="text-white">{selectedTripulante.cir.data_emissao}</p>
                                            </div>
                                            <div>
                                                <p className="text-slate-400">Validade</p>
                                                <p className={`font-bold ${selectedTripulante.cir.status === 'vencido' ? 'text-red-400' : selectedTripulante.cir.status === 'a_vencer' ? 'text-yellow-400' : 'text-green-400'}`}>
                                                    {selectedTripulante.cir.data_validade}
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-slate-500 text-sm">CIR não cadastrada</p>
                                    )}
                                </CardContent>
                            </Card>

                            {/* ASOs */}
                            <Card className="bg-slate-800/50 border-slate-700">
                                <CardHeader className="pb-2 flex flex-row justify-between items-center">
                                    <CardTitle className="text-white text-sm flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-green-400" />
                                        ASO - Atestados de Saúde Ocupacional ({selectedTripulante.asos?.length || 0})
                                    </CardTitle>
                                    {hasRole(['admin', 'gestor', 'operador']) && (
                                        <Button size="sm" variant="outline" onClick={() => setAsoDialogOpen(true)} className="border-slate-600">
                                            <Plus className="w-3 h-3 mr-1" />
                                            Novo ASO
                                        </Button>
                                    )}
                                </CardHeader>
                                <CardContent>
                                    {selectedTripulante.asos?.length > 0 ? (
                                        <div className="space-y-2">
                                            {selectedTripulante.asos.map((aso) => (
                                                <div 
                                                    key={aso.id} 
                                                    className={`p-3 rounded border ${aso.status === 'vencido' ? 'border-red-600 bg-red-900/10' : aso.status === 'a_vencer' ? 'border-yellow-600 bg-yellow-900/10' : 'border-slate-600 bg-slate-700/50'}`}
                                                >
                                                    <div className="flex justify-between items-start">
                                                        <div className="grid grid-cols-4 gap-4 text-sm flex-1">
                                                            <div>
                                                                <p className="text-slate-400">Tipo</p>
                                                                <p className="text-white">{tipoAsoLabels[aso.tipo_aso] || aso.tipo_aso}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-slate-400">Data Exame</p>
                                                                <p className="text-white">{aso.data_exame}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-slate-400">Validade</p>
                                                                <p className={`font-bold ${aso.status === 'vencido' ? 'text-red-400' : aso.status === 'a_vencer' ? 'text-yellow-400' : 'text-green-400'}`}>
                                                                    {aso.data_validade}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <p className="text-slate-400">Médico</p>
                                                                <p className="text-white">{aso.medico_responsavel}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-slate-500 text-sm">Nenhum ASO cadastrado</p>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Embarques */}
                            <Card className="bg-slate-800/50 border-slate-700">
                                <CardHeader className="pb-2 flex flex-row justify-between items-center">
                                    <CardTitle className="text-white text-sm flex items-center gap-2">
                                        <Ship className="w-4 h-4 text-blue-400" />
                                        Histórico de Embarques ({selectedTripulante.embarques?.length || 0})
                                    </CardTitle>
                                    {hasRole(['admin', 'gestor', 'operador']) && !selectedTripulante.embarque_atual && (
                                        <Button size="sm" variant="outline" onClick={() => setEmbarqueDialogOpen(true)} className="border-slate-600">
                                            <Anchor className="w-3 h-3 mr-1" />
                                            Embarcar
                                        </Button>
                                    )}
                                </CardHeader>
                                <CardContent>
                                    {selectedTripulante.embarques?.length > 0 ? (
                                        <div className="space-y-2">
                                            {selectedTripulante.embarques.map((emb) => (
                                                <div 
                                                    key={emb.id} 
                                                    className={`p-3 rounded border ${emb.status_lotacao === 'embarcado' ? 'border-blue-600 bg-blue-900/10' : 'border-slate-600 bg-slate-700/50'}`}
                                                >
                                                    <div className="flex justify-between items-center">
                                                        <div className="grid grid-cols-5 gap-4 text-sm flex-1">
                                                            <div>
                                                                <p className="text-slate-400">Embarcação</p>
                                                                <p className="text-white">{emb.embarcacao_nome}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-slate-400">Função</p>
                                                                <p className="text-white">{funcaoLabels[emb.funcao_bordo] || emb.funcao_bordo}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-slate-400">Embarque</p>
                                                                <p className="text-white">{emb.data_embarque}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-slate-400">Desembarque</p>
                                                                <p className="text-white">{emb.data_desembarque || '-'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-slate-400">Status</p>
                                                                <span className={`px-2 py-0.5 rounded text-xs ${emb.status_lotacao === 'embarcado' ? 'bg-blue-600' : 'bg-slate-600'}`}>
                                                                    {emb.status_lotacao === 'embarcado' ? 'Embarcado' : 'Desembarcado'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        {emb.status_lotacao === 'embarcado' && hasRole(['admin', 'gestor', 'operador']) && (
                                                            <Button size="sm" variant="destructive" onClick={() => handleDesembarcar(emb.id)}>
                                                                Desembarcar
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-slate-500 text-sm">Nenhum embarque registrado</p>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Dialog: CIR */}
            <Dialog open={cirDialogOpen} onOpenChange={setCirDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white">Cadastrar/Editar CIR</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveCir} className="space-y-4">
                        <div>
                            <Label className="text-slate-300">Número da CIR *</Label>
                            <Input 
                                value={cirData.numero_cir}
                                onChange={(e) => setCirData(c => ({ ...c, numero_cir: e.target.value }))}
                                className="bg-slate-800 border-slate-700 text-white"
                                required
                            />
                        </div>
                        <div>
                            <Label className="text-slate-300">Categoria da CIR *</Label>
                            <Input 
                                value={cirData.categoria_cir}
                                onChange={(e) => setCirData(c => ({ ...c, categoria_cir: e.target.value }))}
                                className="bg-slate-800 border-slate-700 text-white"
                                required
                            />
                        </div>
                        <div>
                            <Label className="text-slate-300">Órgão Emissor</Label>
                            <Select value={cirData.orgao_emissor} onValueChange={(v) => setCirData(c => ({ ...c, orgao_emissor: v }))}>
                                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="capitania_portos">Capitania dos Portos</SelectItem>
                                    <SelectItem value="dpc">DPC</SelectItem>
                                    <SelectItem value="outros">Outros</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-slate-300">Data de Emissão</Label>
                                <Input 
                                    type="date"
                                    value={cirData.data_emissao}
                                    onChange={(e) => setCirData(c => ({ ...c, data_emissao: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">Data de Validade *</Label>
                                <Input 
                                    type="date"
                                    value={cirData.data_validade}
                                    onChange={(e) => setCirData(c => ({ ...c, data_validade: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    required
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <Button type="button" variant="outline" onClick={() => setCirDialogOpen(false)} className="border-slate-700">
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700">
                                Salvar
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Dialog: ASO */}
            <Dialog open={asoDialogOpen} onOpenChange={setAsoDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white">Novo ASO</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveAso} className="space-y-4">
                        <div>
                            <Label className="text-slate-300">Tipo de ASO *</Label>
                            <Select value={asoData.tipo_aso} onValueChange={(v) => setAsoData(a => ({ ...a, tipo_aso: v }))}>
                                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(tipoAsoLabels).map(([key, label]) => (
                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-slate-300">Data do Exame *</Label>
                                <Input 
                                    type="date"
                                    value={asoData.data_exame}
                                    onChange={(e) => setAsoData(a => ({ ...a, data_exame: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    required
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">Data de Validade *</Label>
                                <Input 
                                    type="date"
                                    value={asoData.data_validade}
                                    onChange={(e) => setAsoData(a => ({ ...a, data_validade: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    required
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-slate-300">Médico Responsável *</Label>
                            <Input 
                                value={asoData.medico_responsavel}
                                onChange={(e) => setAsoData(a => ({ ...a, medico_responsavel: e.target.value }))}
                                className="bg-slate-800 border-slate-700 text-white"
                                required
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-slate-300">CRM</Label>
                                <Input 
                                    value={asoData.crm_medico}
                                    onChange={(e) => setAsoData(a => ({ ...a, crm_medico: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">Clínica</Label>
                                <Input 
                                    value={asoData.clinica_responsavel}
                                    onChange={(e) => setAsoData(a => ({ ...a, clinica_responsavel: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-slate-300">Observações</Label>
                            <Textarea 
                                value={asoData.observacoes}
                                onChange={(e) => setAsoData(a => ({ ...a, observacoes: e.target.value }))}
                                className="bg-slate-800 border-slate-700 text-white"
                                rows={2}
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <Button type="button" variant="outline" onClick={() => setAsoDialogOpen(false)} className="border-slate-700">
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-green-600 hover:bg-green-700">
                                Adicionar ASO
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Dialog: Embarque */}
            <Dialog open={embarqueDialogOpen} onOpenChange={setEmbarqueDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white">Registrar Embarque</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateEmbarque} className="space-y-4">
                        <div>
                            <Label className="text-slate-300">Tipo de Embarcação *</Label>
                            <Select value={embarqueData.embarcacao_tipo} onValueChange={(v) => setEmbarqueData(e => ({ ...e, embarcacao_tipo: v, embarcacao_id: '' }))}>
                                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="empurrador">Empurrador</SelectItem>
                                    <SelectItem value="balsa">Balsa</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-slate-300">Embarcação *</Label>
                            <Select value={embarqueData.embarcacao_id} onValueChange={(v) => setEmbarqueData(e => ({ ...e, embarcacao_id: v }))}>
                                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                    <SelectValue placeholder="Selecione..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {getEmbarcacoes(embarqueData.embarcacao_tipo).map((emb) => (
                                        <SelectItem key={emb.id} value={emb.id}>{emb.nome}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-slate-300">Função a Bordo *</Label>
                            <Select value={embarqueData.funcao_bordo} onValueChange={(v) => setEmbarqueData(e => ({ ...e, funcao_bordo: v }))}>
                                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(funcaoLabels).map(([key, label]) => (
                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-slate-300">Data de Embarque *</Label>
                                <Input 
                                    type="date"
                                    value={embarqueData.data_embarque}
                                    onChange={(e) => setEmbarqueData(d => ({ ...d, data_embarque: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    required
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">Previsão Desembarque</Label>
                                <Input 
                                    type="date"
                                    value={embarqueData.data_prevista_desembarque}
                                    onChange={(e) => setEmbarqueData(d => ({ ...d, data_prevista_desembarque: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-slate-300">Tipo de Escala</Label>
                            <Select value={embarqueData.tipo_escala} onValueChange={(v) => setEmbarqueData(e => ({ ...e, tipo_escala: v }))}>
                                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(escalaLabels).map(([key, label]) => (
                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <Button type="button" variant="outline" onClick={() => setEmbarqueDialogOpen(false)} className="border-slate-700">
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                                Registrar Embarque
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default TripulantesPage;
